/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ExternalLink } from "lucide-react";

const internalLinks = [
  { id: "about", label: "About" },
  { id: "pillars", label: "What I Do" },
  { id: "services", label: "Services" },
  { id: "products", label: "Products" },
  { id: "contact", label: "Connect" },
];

const externalLinks = [
  { href: "https://www.taxsetu.ai", label: "TaxSetu.ai", testid: "nav-external-taxsetu" },
  { href: "https://practiq.taxsetu.ai", label: "PractiQ", testid: "nav-external-practiq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ink-800/75 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <button
          data-testid="brand-logo"
          onClick={() => goTo("hero")}
          className="flex items-center gap-3 group"
        >
          <span className="relative w-11 h-11 rounded-xl overflow-hidden ring-1 ring-white/10 bg-black">
            <img
              src="https://customer-assets.emergentagent.com/job_shivangi-digital/artifacts/z1cbny6n_619e7a1a0cf2c56a71b570e2.png"
              alt="Shivangi Mittal Logo"
              className="w-full h-full object-cover"
            />
            <span className="absolute inset-0 rounded-xl blur-md opacity-50 -z-10"
                  style={{ background: "linear-gradient(135deg, #16D1C8, #FF2D7A)" }} />
          </span>
          <div className="flex flex-col items-start leading-none">
            <span className="font-heading font-semibold text-white text-base tracking-tight">Shivangi Mittal</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-teal-300 mt-1">Tax · Tech · Transform</span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {internalLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goTo(l.id)}
              className="px-3 py-2 text-sm text-white/75 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-3 right-3 -bottom-0.5 h-px scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300"
                    style={{ background: "linear-gradient(90deg, #16D1C8, #FF2D7A)" }} />
            </button>
          ))}
          <span className="w-px h-5 bg-white/10 mx-2" />
          {externalLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={l.testid}
              className="px-3 py-2 text-sm text-teal-300 hover:text-teal-200 inline-flex items-center gap-1.5 transition-colors"
            >
              {l.label}
              <ExternalLink size={12} className="opacity-70" />
            </a>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid="nav-cta-connect"
            onClick={() => goTo("contact")}
            className="btn-primary !py-2.5 !px-5 text-sm"
          >
            Let's Connect
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden border-t border-white/5 bg-ink-900/95 backdrop-blur-xl"
          >
            <div className="container-x py-6 flex flex-col gap-1">
              {internalLinks.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => goTo(l.id)}
                  className="text-left px-2 py-3 text-white/85 border-b border-white/5"
                >
                  {l.label}
                </button>
              ))}
              {externalLinks.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-${l.testid}`}
                  className="px-2 py-3 text-teal-300 border-b border-white/5 inline-flex items-center gap-2"
                >
                  {l.label}
                  <ExternalLink size={12} className="opacity-70" />
                </a>
              ))}
              <button
                data-testid="mobile-cta-connect"
                onClick={() => goTo("contact")}
                className="btn-primary mt-4 justify-center"
              >
                Let's Connect
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
